<template>
  <div class="w-full flex justify-content-center">
    <div class="w-11">
      <h2>Acta SIFCOP</h2>

      <div class="header-block">
        <SifcopFilter
          :filters="filters"
          :departamentalOptions="departamentalOptions"
          :isLoading="isLoading"
          @apply="handleApplyFilters"
          @clear="handleClearFilters"
        />

        <Button
          @click="handleCreateForm"
          class="action-btn new-register-btn"
          label="Nuevo formulario"
        />
      </div>

      <div v-if="activeFilterChips.length" class="filter-display mt-3">
        <Chip
          v-for="chip in activeFilterChips"
          :key="chip.key"
          :label="chip.label"
          removable
          class="filter-item"
          @remove="removeFilter(chip.key)"
        />
      </div>

      <SifcopTable
        :items="paginatedItems"
        :isLoading="isLoading"
        :generatingPdfId="generatingPdfId"
        @view="handleView"
        @generate-pdf="handleGeneratePdf"
      />

      <Paginator
        :records="paginatedItems"
        :totalRecords="totalRecords"
        :pageSize="pageSize"
        :currentPage="currentPage"
        :windowSize="5"
        @prevPage="prevPage"
        @nextPage="nextPage"
        @goToPage="goToPage"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import axios from "axios";
import { storeToRefs } from "pinia";
import { useRouterCompat } from "@/composables";
import Paginator from "@/components/paginator.vue";
import { useBreadcrumbStore } from "@/store/Pinia/useBreadcrumbStore";
import { useSifcopListStore } from "@/store/Pinia/useSifcopListStore";
import { mapBackendDocToForm } from "@/store/Pinia/useSifcopStore";
import {
  getCachedSifcopDetail,
  getOrFetchSifcopDetail,
  prefetchSifcopDetail,
} from "@/utils/sifcopDetailCache";
import { generateSifcopPdf } from "@/utils/sifcopPdf";
import SifcopFilter from "@/pages/sifcop/SifcopFilter.vue";
import SifcopTable from "@/pages/sifcop/SifcopTable.vue";

const breadcrumbStore = useBreadcrumbStore();
const { router } = useRouterCompat();
const sifcopListStore = useSifcopListStore();
const generatingPdfId = ref(null);
const {
  filters,
  isLoading,
  departamentalOptions,
  activeFilterChips,
  paginatedItems,
  totalRecords,
  pageSize,
  currentPage,
} = storeToRefs(sifcopListStore);

const { setFilters, clearFilters, removeFilter, prevPage, nextPage, goToPage, fetchItems } =
  sifcopListStore;

const handleApplyFilters = (nextFilters) => {
  setFilters(nextFilters);
};

const handleClearFilters = () => {
  clearFilters();
};

const handleCreateForm = () => {
  router.push("/sifcop/new");
};

const handleView = (data) => {
  router.push(`/sifcop/${data.id}`);
};

const buildSifcopDetailEndpoint = (id) => {
  const base = String(process.env.VUE_APP_BACKEND_ENVIRONMENT || "").replace(/\/$/, "");
  const sifcopBase = base.replace(/\/api\/v1(?=\/|$)/i, "/api/v3");
  return `${sifcopBase}/sifcop/${id}`;
};

const fetchSifcopDetailDoc = async (id) => {
  if (!id) return null;

  const response = await axios.get(buildSifcopDetailEndpoint(id), {
    withCredentials: true,
  });

  return response?.data?.doc || response?.data?.data?.doc || null;
};

const prefetchVisibleItems = (items = []) => {
  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item?.id) return;
    prefetchSifcopDetail(item.id, fetchSifcopDetailDoc);
  });
};

const openPdfPreviewWindow = () => {
  const previewWindow = window.open("", "_blank");

  if (!previewWindow || previewWindow.closed) {
    return null;
  }

  previewWindow.document.write(`
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <title>Generando PDF...</title>
        <style>
          html, body {
            margin: 0;
            width: 100%;
            height: 100%;
            background: #f8fafc;
            font-family: Arial, sans-serif;
          }

          body {
            display: flex;
            align-items: center;
            justify-content: center;
            color: #0b5195;
          }

          #viewer {
            display: none;
            width: 100%;
            height: 100%;
            border: 0;
            background: #ffffff;
          }
        </style>
      </head>
      <body>
        <div id="status">Generando PDF...</div>
        <iframe id="viewer" title="Vista previa PDF"></iframe>
        <script>
          window.addEventListener("message", function(event) {
            if (event.origin !== window.location.origin) return;
            if (!event.data || event.data.type !== "sifcop-pdf-ready") return;

            var status = document.getElementById("status");
            var viewer = document.getElementById("viewer");

            document.title = event.data.title || "PDF SIFCOP";
            if (status) status.style.display = "none";
            if (viewer) {
              viewer.style.display = "block";
              viewer.src = event.data.pdfUrl;
            }
          });
        <\/script>
      </body>
    </html>
  `);
  previewWindow.document.close();

  return previewWindow;
};

const GRACE_PERIOD_MS = 3000;

const handleGeneratePdf = async (data) => {
  if (generatingPdfId.value || !data?.id) return;

  generatingPdfId.value = data.id;

  let previewWindow = null;

  try {
    const cachedDoc = getCachedSifcopDetail(data.id);
    if (cachedDoc) {
      await generateSifcopPdf({
        formData: mapBackendDocToForm(cachedDoc),
        outputFileName: `sifcop-${data.identificacion || data.id || Date.now()}.pdf`,
      });
      return;
    }

    const fetchPromise = getOrFetchSifcopDetail(data.id, fetchSifcopDetailDoc);

    const graceTimeout = setTimeout(() => {
      previewWindow = openPdfPreviewWindow();
    }, GRACE_PERIOD_MS);

    const doc = await fetchPromise;
    clearTimeout(graceTimeout);

    await generateSifcopPdf({
      formData: mapBackendDocToForm(doc),
      outputFileName: `sifcop-${data.identificacion || data.id || Date.now()}.pdf`,
      previewWindow,
    });
  } catch (error) {
    if (previewWindow && !previewWindow.closed) {
      previewWindow.close();
    }

    console.error("Error al generar PDF del listado SIFCOP:", error);
  } finally {
    generatingPdfId.value = null;
  }
};

watch(
  paginatedItems,
  (items) => {
    prefetchVisibleItems(items);
  },
  { immediate: true }
);

onMounted(async () => {
  breadcrumbStore.setItems([{ label: "SIFCOP", to: "/sifcop" }]);
  await fetchItems();
});
</script>

<style scoped>
.header-block {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  min-width: 160px;
  height: 36px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  color: white;
  border: none;
}

.new-register-btn {
  background-color: var(--Primary, #0b5195);
}

.new-register-btn:hover {
  opacity: 0.9;
  background-color: var(--Primary, #0b5095c5) !important;
  cursor: pointer;
}

.filter-display {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-item {
  background-color: rgb(239, 246, 251);
  color: rgb(11, 81, 149);
  padding: 6px 12px;
  border-radius: 9999px;
}

::v-deep(.p-chip .p-chip-remove-icon) {
  color: red !important;
}
</style>
